import React, { useState, useEffect } from 'react';
import { 
  FiCalendar as Calendar, 
  FiAlertCircle as AlertCircle, 
  FiCheckCircle as CheckCircle,
  FiClock as Clock,
  FiX as X,
  FiPhone as Phone,
  FiMail as Mail
} from 'react-icons/fi';
import { format, differenceInDays, isAfter, isBefore, addDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { currentPolicies } from '../utils/policyStatus';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';

const RenewalNotifications = ({ onClose, isWidget = false }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, urgent, upcoming

  useEffect(() => {
    if (user?.policies) {
      // Only a policy that has not ended can be renewed.
      const renewalNotifications = generateRenewalNotifications(currentPolicies(user.policies));
      setNotifications(renewalNotifications);
    }
  }, [user]);

  const generateRenewalNotifications = (policies) => {
    const today = new Date();
    const notifications = [];

    policies.forEach(policy => {
      const expirationDate = new Date(policy.expirationDate);
      const daysUntilExpiry = differenceInDays(expirationDate, today);

      // Generate notifications for policies expiring within 90 days
      if (daysUntilExpiry >= 0 && daysUntilExpiry <= 90) {
        let urgency = 'low';
        let message = '';
        let actionRequired = false;

        if (daysUntilExpiry <= 7) {
          urgency = 'critical';
          message = `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}! Immediate action required.`;
          actionRequired = true;
        } else if (daysUntilExpiry <= 30) {
          urgency = 'high';
          message = `Expires in ${daysUntilExpiry} days. Please contact us to renew.`;
          actionRequired = true;
        } else if (daysUntilExpiry <= 60) {
          urgency = 'medium';
          message = `Expires in ${daysUntilExpiry} days. Start planning your renewal.`;
        } else {
          urgency = 'low';
          message = `Expires in ${daysUntilExpiry} days. Renewal available soon.`;
        }

        notifications.push({
          id: policy.id,
          policyNumber: policy.policyNumber,
          policyType: policy.policyType,
          vendor: policy.vendor,
          expirationDate: policy.expirationDate,
          daysUntilExpiry,
          urgency,
          message,
          actionRequired,
          premium: policy.premium,
          paymentFrequency: policy.paymentFrequency
        });
      }
    });

    // Sort by urgency (critical first) and then by days until expiry
    return notifications.sort((a, b) => {
      const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      }
      return a.daysUntilExpiry - b.daysUntilExpiry;
    });
  };

  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-900',
          badge: 'bg-red-100 text-red-800',
          icon: 'text-red-600'
        };
      case 'high':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-900',
          badge: 'bg-orange-100 text-orange-800',
          icon: 'text-orange-600'
        };
      case 'medium':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-900',
          badge: 'bg-yellow-100 text-yellow-800',
          icon: 'text-yellow-600'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          badge: 'bg-blue-100 text-blue-800',
          icon: 'text-blue-600'
        };
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'urgent') return notif.urgency === 'critical' || notif.urgency === 'high';
    if (filter === 'upcoming') return notif.urgency === 'medium' || notif.urgency === 'low';
    return true;
  });

  const urgentCount = notifications.filter(n => n.urgency === 'critical' || n.urgency === 'high').length;

  // Widget version (compact)
  if (isWidget) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Calendar size={20} className="text-[#DC143C]" />
            Renewal Reminders
          </h3>
          {urgentCount > 0 && (
            <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
              {urgentCount} Urgent
            </span>
          )}
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle size={32} className="mx-auto mb-2 text-green-500" />
            <p className="text-sm">All policies are up to date!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.slice(0, 3).map((notification) => {
              const styles = getUrgencyStyles(notification.urgency);
              return (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border ${styles.border} ${styles.bg}`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className={styles.icon} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">
                        {notification.policyType}
                      </div>
                      <div className="text-xs text-gray-600 mb-1">
                        {notification.vendor} • {notification.policyNumber}
                      </div>
                      <div className={`text-xs ${styles.text} font-medium`}>
                        {notification.message}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredNotifications.length > 3 && (
              <button className="w-full text-sm text-[#DC143C] font-semibold hover:underline">
                View All {filteredNotifications.length} Renewals →
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full modal version
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Calendar size={28} className="text-[#DC143C]" />
              Policy Renewal Notifications
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Stay on top of your policy renewals
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {notifications.length}
              </div>
              <div className="text-xs text-gray-600">Total Renewals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {notifications.filter(n => n.urgency === 'critical').length}
              </div>
              <div className="text-xs text-gray-600">Critical</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {notifications.filter(n => n.urgency === 'high').length}
              </div>
              <div className="text-xs text-gray-600">High Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {notifications.filter(n => n.urgency === 'medium' || n.urgency === 'low').length}
              </div>
              <div className="text-xs text-gray-600">Upcoming</div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-6 py-4 border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-[#DC143C] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('urgent')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'urgent'
                ? 'bg-[#DC143C] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Urgent ({urgentCount})
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'upcoming'
                ? 'bg-[#DC143C] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Upcoming ({notifications.length - urgentCount})
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                All Clear!
              </h3>
              <p className="text-gray-600">
                No renewal notifications in this category
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => {
                const styles = getUrgencyStyles(notification.urgency);
                return (
                  <div
                    key={notification.id}
                    className={`p-6 rounded-xl border-2 ${styles.border} ${styles.bg} hover:shadow-md transition-all duration-200`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <AlertCircle size={24} className={styles.icon} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-bold text-gray-900">
                              {notification.policyType}
                            </h3>
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${styles.badge} uppercase`}>
                              {notification.urgency}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            {notification.vendor} • Policy #{notification.policyNumber}
                          </div>
                          <div className={`text-sm font-semibold ${styles.text}`}>
                            {notification.message}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {notification.daysUntilExpiry}
                        </div>
                        <div className="text-xs text-gray-600">days left</div>
                      </div>
                    </div>

                    {/* Policy Details */}
                    <div className="bg-white/50 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Expiration Date:</span>
                          <div className="font-semibold text-gray-900">
                            {format(new Date(notification.expirationDate), 'MMMM dd, yyyy')}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Current Premium:</span>
                          <div className="font-semibold text-gray-900">
                            ${notification.premium.toFixed(2)}/{notification.paymentFrequency}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={AGENCY_PHONE_TEL}
                        className="flex-1 bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] transition-colors flex items-center justify-center gap-2"
                      >
                        <Phone size={18} />
                        Call to Renew
                      </a>
                      <a
                        href="mailto:info@pinoygeneralinsurance.com"
                        className="flex-1 border border-[#0038A8] text-[#0038A8] py-3 px-4 rounded-lg font-semibold hover:bg-[#0038A8] hover:text-white transition-colors flex items-center justify-center gap-2"
                      >
                        <Mail size={18} />
                        Email Us
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-blue-50 border-t border-blue-200 p-6">
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                Don't Wait Until the Last Minute
              </h4>
              <p className="text-sm text-blue-800">
                Contact us at least 30 days before your policy expires to ensure uninterrupted coverage 
                and potentially better rates. Call <a href={AGENCY_PHONE_TEL} className="font-bold underline">{AGENCY_PHONE_DISPLAY}</a> today!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalNotifications;